import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The VehicleWheel3D editor gizmo, ported from Godot's
 * editor/scene/3d/gizmos/physics/vehicle_body_3d_gizmo_plugin.cpp: the wheel circle,
 * a spring coil, the suspension travel line, an axle tick at each end, and a
 * forward arrow (+Z) that shows a mirrored or back-to-front wheel.
 */
import { useMemo } from 'react';
import { CollisionDebugLines } from '../../shared/CollisionDebugLines';
import { degToRad } from '../../../../godot/math/math.js';
/** Godot's `skip`: degrees per circle segment. */
const SKIP = 10;
/** Godot's `springsec`: coil turns drawn along the suspension. */
const SPRING_SECTIONS = 4;
/** Godot scales the coil down by this factor. */
const COIL_SCALE = 0.2;
export function WheelGizmo({ radius, restLength }) {
    const positions = useMemo(() => buildWheelGizmo(radius, restLength), [radius, restLength]);
    return _jsx(CollisionDebugLines, { positions: positions });
}
function buildWheelGizmo(radius, restLength) {
    const points = [];
    const push = (x, y, z) => {
        points.push(x, y, z);
    };
    const r = radius;
    const t = restLength * 5;
    const section = t / SPRING_SECTIONS;
    for (let i = 0; i <= 360; i += SKIP) {
        const ra = degToRad(i);
        const rb = degToRad(i + SKIP);
        const ax = Math.sin(ra) * r;
        const ay = Math.cos(ra) * r;
        const bx = Math.sin(rb) * r;
        const by = Math.cos(rb) * r;
        // The wheel circle, in the YZ plane. i runs 0…360 inclusive, so the last
        // segment overlaps the first, as in Godot: the vertex buffer stays in step.
        push(0, ax, ay);
        push(0, bx, by);
        // The spring coil, wound around the suspension axis.
        for (let j = 0; j < SPRING_SECTIONS; j += 1) {
            push(ax * COIL_SCALE, ((i / 360) * section + j * section) * COIL_SCALE, ay * COIL_SCALE);
            push(bx * COIL_SCALE, (((i + SKIP) / 360) * section + j * section) * COIL_SCALE, by * COIL_SCALE);
        }
    }
    // Suspension travel.
    push(0, 0, 0);
    push(0, restLength, 0);
    // Axle ticks at both ends of the travel.
    push(r * 0.2, restLength, 0);
    push(-r * 0.2, restLength, 0);
    push(r * 0.2, 0, 0);
    push(-r * 0.2, 0, 0);
    // Forward arrow.
    push(0, -r, 0);
    push(0, -r, r * 2);
    push(0, -r, r * 2);
    push(r * 2 * 0.2, -r, r * 2 * 0.8);
    push(0, -r, r * 2);
    push(-r * 2 * 0.2, -r, r * 2 * 0.8);
    return new Float32Array(points);
}
//# sourceMappingURL=WheelGizmo.js.map